import React from 'react';
import Button from '@mui/material/Button';
import { Box, Typography } from "@mui/material";
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';

function CopyMoveMemoControl({
    cellMemoMode,       // "copy" | "move"
    cellMemoPhase,      // "source" | "target"
    cellMemoSourceCells,
    cellMemoTargetCells,
    onAdvanceToTarget,
    onPaste,
    onCancel,
    onBackToSource,
    colors,
    socketConnected,
    cellMemoMap,
}) {
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    // Check if any target cell has an existing non-empty memo
    const hasOverwriteTargets = cellMemoTargetCells.some(t => {
        if (!t.existingMemoId) return false;
        const targetMemo = cellMemoMap?.[t.date]?.[t._time]?.[t.zoom_id]?.memo;
        return targetMemo && targetMemo.trim() !== "";
    });

    const handlePasteClick = () => {
        if (hasOverwriteTargets) {
            setConfirmOpen(true);
        } else {
            onPaste();
        }
    };

    return (
        <>
            {/* Status text */}
            <Box sx={{ flex: 2, display: "flex", alignItems: "center", justifyContent: "center", px: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", color: colors.primary[100] }}>
                    {cellMemoMode === "copy" ? "COPY" : "MOVE"} |{" "}
                    {cellMemoPhase === "source"
                        ? `Select source cells (${cellMemoSourceCells.length} selected)`
                        : `Select target cells (${cellMemoTargetCells.length}/${cellMemoSourceCells.length})`
                    }
                </Typography>
            </Box>

            {/* Phase-specific buttons */}
            {cellMemoPhase === "source" ? (
                <>
                    <Button onClick={onAdvanceToTarget}
                        disabled={cellMemoSourceCells.length === 0}
                        sx={{ flex: 1, height: "100%", bgcolor: colors.greenAccent[800], borderRadius: 0,
                            textTransform: "none",
                            '&:hover': { bgcolor: colors.greenAccent[900] },
                            '&:disabled': { bgcolor: colors.grey[700] },
                        }}>
                        Next
                    </Button>
                    <Button onClick={onCancel}
                        sx={{ flex: 1, height: "100%", bgcolor: colors.redAccent[800], borderRadius: 0,
                            textTransform: "none",
                            '&:hover': { bgcolor: colors.redAccent[900] },
                        }}>
                        Cancel
                    </Button>
                </>
            ) : (
                <>
                    <Button onClick={onBackToSource}
                        sx={{ flex: 1, height: "100%", bgcolor: colors.blueAccent[800], borderRadius: 0,
                            textTransform: "none",
                            '&:hover': { bgcolor: colors.blueAccent[900] },
                        }}>
                        Back
                    </Button>
                    <Button onClick={handlePasteClick}
                        disabled={cellMemoTargetCells.length !== cellMemoSourceCells.length || !socketConnected}
                        sx={{ flex: 1, height: "100%", bgcolor: colors.greenAccent[800], borderRadius: 0,
                            textTransform: "none",
                            '&:hover': { bgcolor: colors.greenAccent[900] },
                            '&:disabled': { bgcolor: colors.grey[700] },
                        }}>
                        Paste
                    </Button>
                    <Button onClick={onCancel}
                        sx={{ flex: 1, height: "100%", bgcolor: colors.redAccent[800], borderRadius: 0,
                            textTransform: "none",
                            '&:hover': { bgcolor: colors.redAccent[900] },
                        }}>
                        Cancel
                    </Button>
                </>
            )}

            {/* Overwrite confirmation modal */}
            <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}
                closeAfterTransition slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 500 } }}>
                <Fade in={confirmOpen}>
                    <Box sx={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)', width: 400,
                        bgcolor: colors.primary[600], border: '2px solid #000',
                        boxShadow: 24, p: 4,
                    }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Some target cells already have memos. They will be overwritten. Continue?
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                            <Button variant="contained" color="secondary"
                                onClick={() => { setConfirmOpen(false); onPaste(); }}>
                                Yes, Paste
                            </Button>
                            <Button variant="contained" color="error"
                                onClick={() => setConfirmOpen(false)}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </>
    );
}

export default CopyMoveMemoControl;
